import { Theme, ThemeProvider } from '@emotion/react';
import { ErrorBoundary } from '@sentry/react';
import { isEqual, merge } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiAppParams } from 'typings/apiApp';
import { isConfigBuilder, request } from '@/modules/api';
import { loadTranslations } from '@/modules/app/data';
import { DEFAULT_TEST_ENV_HOSTNAMES, theme as defaultTheme, useEvent } from '@/modules/common';
import { PageType } from '@/modules/models';
import {
  bridgeAtom,
  DEFAULT_BRIDGE_VALUE,
  isOpenChatAtom,
  Provider,
  store,
  useAtom,
  useReset,
  useSetValue,
  viewedProductsAtom,
} from '@/store';
import { AppEvents, getProductUrl, parseShopifyUrl } from '@/utils';
import { App } from './App';

if (process.env.NODE_ENV === 'production') {
  import('@/utils/sentry');
}

type AppContainerProps = { config: ApiAppParams };

function AppContainer(props: AppContainerProps) {
  const [theme, setTheme] = useState<Theme>(merge({}, defaultTheme, props.config.theme));

  const resetBridgeAtom = useReset(bridgeAtom);
  const [bridge, setBridge] = useAtom(bridgeAtom);
  const [viewedProducts, setViewedProducts] = useAtom(viewedProductsAtom);
  const setIsOpenChat = useSetValue(isOpenChatAtom);
  const { pageType, productId } = parseShopifyUrl(window.location.href);
  const isViewed = viewedProducts?.includes(productId);
  const hasFetchedTranslations = useRef(false);

  const fetchTranslations = useCallback(
    async (shouldFetchTranslations: boolean) => {
      const { translations } = await loadTranslations(
        props.config.localization ?? 'en',
        props.config.projectName?.toLowerCase(),
        shouldFetchTranslations
      );
      return translations ?? {};
    },
    [props.config.localization, props.config.projectName]
  );

  const getBridgeParams = useCallback(
    (config?: Partial<ApiAppParams>) => {
      const currentBridgeSnapshot = store.get(bridgeAtom);
      const isBridgeEmpty =
        isEqual(currentBridgeSnapshot, DEFAULT_BRIDGE_VALUE) || !Object.keys(currentBridgeSnapshot).length;
      const {
        apiUrl,
        apiKey,
        theme,
        pageType: bridgePageType,
        translations: configTranslations,
        shouldFetchTranslations,
        ...others
      } = merge({}, isBridgeEmpty ? props.config : currentBridgeSnapshot, config);
      request.setBaseUrl(apiUrl);

      const abTestDisplay = others.abTestConfig?.client
        ? {
            display: others.abTestConfig.client.group === others.abTestConfig.client.groupsList?.[0] ? 'none' : 'flex',
          }
        : undefined;

      setTheme((p) =>
        merge(
          {},
          p,
          theme,
          abTestDisplay && {
            widget: { wrapper: { styles: abTestDisplay } },
            container: { styles: abTestDisplay },
          }
        )
      );

      const url = getProductUrl();
      const { productId, variantId, pageType } = parseShopifyUrl(url);

      if (!hasFetchedTranslations.current) {
        fetchTranslations(Boolean(shouldFetchTranslations)).then((translations) => {
          hasFetchedTranslations.current = true;
          setBridge((prev) => {
            return merge({}, prev, others, {
              apiUrl,
              apiKey,
              pageType: bridgePageType ?? pageType,
              productId,
              variantId,
              translations: merge({}, translations, configTranslations),
            });
          });
        });
      } else {
        setBridge((prev) => {
          return merge({}, prev, others, {
            apiUrl,
            apiKey,
            pageType: bridgePageType ?? pageType,
            productId,
            variantId,
            translations: merge({}, configTranslations),
          });
        });
      }
    },
    [fetchTranslations, setBridge, props.config]
  );

  const onResetParams = useCallback(() => {
    resetBridgeAtom();
    hasFetchedTranslations.current = false;
    setTheme(defaultTheme);
    getBridgeParams();
  }, [getBridgeParams]);

  const onUpdateParams = useCallback(
    (params: CustomEvent<Partial<ApiAppParams>>) => {
      getBridgeParams(params.detail);
    },
    [getBridgeParams]
  );

  useEffect(() => {
    getBridgeParams();
  }, [getBridgeParams]);

  useEffect(() => {
    if (!bridge.apiUrl || !bridge.apiKey) {
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const testParam = urlParams.get('test');
    const chatOpenParam = urlParams.get('swChatOpen');
    const isHideMetricsParam = urlParams.get('hideMetrics');

    const isTestEnv = DEFAULT_TEST_ENV_HOSTNAMES.some((testUrl) => window.location.hostname.includes(testUrl));
    const isConfigBuilderPage = isConfigBuilder();
    const { isTestModeAvailable = true } = bridge;

    if (isConfigBuilderPage || (isTestModeAvailable && (testParam === '1' || isTestEnv))) {
      localStorage.setItem('testUser', '1');
    }
    if (chatOpenParam === '1') {
      setIsOpenChat(true);
      sessionStorage.setItem('influencerUser', '1');
    }
    if (isHideMetricsParam === '1' || isConfigBuilderPage) {
      sessionStorage.setItem('hideMetrics', '1');
    }
  }, [bridge]);

  useEffect(() => {
    if (pageType !== PageType.PRODUCT || isViewed) {
      return;
    }

    setViewedProducts((prevState) => [...prevState, productId]);
  }, [isViewed, pageType, productId, setViewedProducts]);

  useEvent({ name: AppEvents.UPDATE_PARAMS, handler: onUpdateParams });
  useEvent({ name: AppEvents.RESET_CONFIG, handler: onResetParams });

  if (!bridge.pageType || !bridge.apiUrl || !bridge.apiKey || !theme) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default function AppContainerProvider(props: AppContainerProps) {
  return (
    <Provider store={store}>
      <AppContainer {...props} />
    </Provider>
  );
}
